import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import DateFilterPopup, { DateFilterValue } from "../Services/DateFilterPopup/DateFilterPopup";
import Pagination from "../Services/PaginationControl/Pagination";
import { getAllDoctorsAPI, UpdateActivateDeactivateDoctor } from "../Api/MarketingApi";
import StatusBadge from "../Common/StatusBadge";
import EditProfilePopup from "../PopUp/DoctorProfilePopup/EditProfilePopup";
import OriginalProfile from "../PopUp/DoctorProfilePopup/OriginalProfile";
import StatusTile from "../Common/StatusTile";
import DoctorProfileTAT from "../Pages/Audit/DoctorProfileTAT/DoctorProfileTAT";
import DoctorTATRow from "../Pages/Audit/DoctorProfileAudit/DoctorProfileAudit";
import Button from "../Common/Button";
import { Toast } from "../Common/Toast";

dayjs.extend(isBetween);

interface Doctor {
  doctorId: number;
  empCode:string;
  source:string;
  name: string;
  namePrefixCode: string;
  designation: string | null;
  locationName: string;
  specialization: string | null;
  doctorStatus: string;
  experienceYears: number;
  createdOn: string;
  createdBy: string;
  overdueDays: number;

}

interface DoctorsAPIResponse {
  doctorlist: Doctor[];
  isEdit: boolean;
  isView: boolean;
  isDelete: boolean;
}

interface TileCounts {
  pending: number;
  draft: number;
  sendback: number;
  approved?: number;  // Marketing Head only
  recheck?: number;   // Marketing Head only
  submitted?: number;
  deactivate?: number; // Other roles only
  left?:number;
}

type StatusType =
  | "submitted"
  | "approved"
  | "rejected"
  | "pending"
  | "draft"
  | "sendback"
  | "recheck"
  |"deactivate"
  |"left";

// ── Icons ─────────────────────────────────────────────────────────────────────

const PendingIcon = (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);

const DraftIcon = (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);

const SubmittedIcon = (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const SendBackIcon = (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const RecheckIcon = (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
  </svg>
);

const ApprovedIcon = (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);
const DeactivatedIcon = (
   <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.536-11.536a1 1 0 00-1.414-1.414L6.05 11.121a1 1 0 101.414 1.414l6.072-6.071z"
      clipRule="evenodd"
    />
  </svg>
  
);
const LeftIcon = (
  <svg
    className="w-6 h-6 text-red-500"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    {/* User */}
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19a4 4 0 00-8 0"
    />
    <circle cx="11" cy="8" r="4" strokeWidth={2} />

    {/* Left Arrow */}
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20 12h-6m0 0l3-3m-3 3l3 3"
    />
  </svg>
);


// ─────────────────────────────────────────────────────────────────────────────

const Home = () => {
  const userName = sessionStorage.getItem("fullName");
  const navigate = useNavigate();
  const roleName = sessionStorage.getItem("roleName");

  const isMarketingHead = roleName === "Marketing Head";
const isContentCreator = roleName === "Content Creator";
  const [doctorsData, setDoctorsData] = useState<DoctorsAPIResponse>({
    doctorlist: [],
    isEdit: false,
    isView: false,
    isDelete: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string>("");

  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>("");
  const [dateFilterValue, setDateFilterValue] = useState<DateFilterValue>({
    type: "",
    from: "",
    to: "",
  });
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);

  const [filters, setFilters] = useState({
    name: "",
    empCode: "",
    Source: "",
    designation: "",
    experience: "",
    locationName: "",
    specialization: "",
    overdueDays: "",
    doctorStatus: "",
    createdOn: "",
    createdBy:"",
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);
  const [expandedDoctorId, setExpandedDoctorId] = useState<number | null>(null);

  const [showTATModal, setShowTATModal] = useState(false);
  const [selectedDoctorForTAT, setSelectedDoctorForTAT] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const [sortConfig, setSortConfig] = useState<{
    key: keyof Doctor | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);

  const doctors = doctorsData.doctorlist;
const [openMenuDoctorId, setOpenMenuDoctorId] = useState<number | null>(null);

const toggleActionMenu = (e: React.MouseEvent, doctorId: number) => {
  e.stopPropagation();
  setOpenMenuDoctorId((prev) => (prev === doctorId ? null : doctorId));
};
const closeActionMenu =()=> setOpenMenuDoctorId(null);

const handleToggleDoctorStatus = async (doctorId: number, currentStatus: string) => {
  try {
    
    const newStatus = currentStatus.toLowerCase() === "approved" ? "D" : "A";
    const statusText =  currentStatus.toLowerCase() === "approved" ? "Deactivate" : "Approve";
    const payload = {
      doctorId,
      status: newStatus,
      event: "Doctor status Update",
      remark: `Doctor ${statusText}`,
      userId: sessionStorage.getItem("userId"),
    };

    await UpdateActivateDeactivateDoctor(payload);
    Toast.success(`Doctor Update successfully`);
    await fetchDoctors();
  } catch (error: any) {
    console.error("Error toggling doctor status:", error);
    const errorMsg = error?.response?.data?.message || 
                    error?.message || 
                    "Failed to update doctor status";
    
    Toast.error(errorMsg);
  }
};

  // ── API ───────────────────────────────────────────────────────────────────

  const fetchDoctors = async () => {
    try {
      setIsLoading(true);
      setApiError("");
      const response = await getAllDoctorsAPI(roleName);
      if (response.data && response.data.doctorlist) {
        setDoctorsData({
          doctorlist: response.data.doctorlist || [],
          isEdit: response.data.isEdit || false,
          isView: response.data.isView || false,
          isDelete: response.data.isDelete || false,
        });
      }
    } catch (error: any) {
      console.error("Error fetching doctors:", error);
      setApiError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch doctors data"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleRowToggle = (doctorId: number) => {
    setExpandedDoctorId((prev) => (prev === doctorId ? null : doctorId));
  };

  const handleStatusTileClick = (status: string) => {
    setSelectedStatusFilter((prev) => (prev === status ? "" : status));
    setCurrentPage(1);
  };

  const handleDateFilterApply = (value: DateFilterValue) => {
    setDateFilterValue(value);
    setCurrentPage(1);
    setIsDateFilterOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({
      name: "",
      designation: "",
        empCode: "",
  Source: "",
      experience: "",
      locationName: "",
      specialization: "",
      overdueDays: "",
      doctorStatus: "",
      createdOn: "",
      createdBy:"",
    });
    setSelectedStatusFilter("");
    setDateFilterValue({ type: "", from: "", to: "" });
    setCurrentPage(1);
  };

  const handleViewDoctor = (doctorId: number) => {
    setIsViewModalOpen(true);
    setSelectedDoctorId(doctorId);
  };

const canEditDoctor = (status: string) => {
  const s = status.toLowerCase();
  // Marketing Head can always edit
  if (isMarketingHead) return true;

  // Nobody can edit approved
  if (s === "approved") return false;
  if (s === "recheck") return false;
  // Content Creator cannot edit submitted
  if (isContentCreator && s === "submitted") return false;
  return true;
};

  const handleEditDoctor = (doctorId: number) => {
    setIsEditModalOpen(true);
    setSelectedDoctorId(doctorId);
  };

  const handleCloseModal = () => {
    fetchDoctors();
    setIsEditModalOpen(false);
    setIsViewModalOpen(false);
    setSelectedDoctorId(null);
  };

  const handleDeleteDoctor = (_doctorId: number) => {
    // Implement delete logic here
  };

  const handleAuditDoctor = (id: number, name: string) => {
    setSelectedDoctorForTAT({ id, name });
    setShowTATModal(true);
  };

  const handleSort = (key: keyof Doctor) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  const sortIcon = (key: keyof Doctor) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  // ── Raw counts ────────────────────────────────────────────────────────────

  const rawCounts = {
    pending:   doctors.filter((d) => d.doctorStatus?.toLowerCase() === "pending").length,
    submitted: doctors.filter((d) => d.doctorStatus?.toLowerCase() === "submitted").length,
    draft:     doctors.filter((d) => d.doctorStatus?.toLowerCase() === "save draft").length,
    sendback:  doctors.filter((d) => d.doctorStatus?.toLowerCase() === "send back").length,
    recheck:   doctors.filter((d) => d.doctorStatus?.toLowerCase() === "recheck").length,
    approved:  doctors.filter((d) => d.doctorStatus?.toLowerCase() === "approved").length,
    deactivate: doctors.filter((d) => d.doctorStatus?.toLowerCase() === "deactivate").length,
    left: doctors.filter((d) => d.doctorStatus?.toLowerCase() === "left").length,


  };

  // ── Role-aware tile counts ────────────────────────────────────────────────
  // Marketing Head → 5 tiles: Pending (pending+submitted), Draft, Approved, Send Back, Re-check
  // Other roles    → 4 tiles: Pending, Draft, Submitted (submitted+recheck), Send Back

  const tileCounts: TileCounts = isMarketingHead
    ? {
        pending:   rawCounts.submitted,
        draft:    rawCounts.draft,
        approved: rawCounts.approved,
        sendback: rawCounts.sendback,
        recheck:  rawCounts.recheck,
        deactivate:rawCounts.deactivate,
        left:rawCounts.left,
      }
    : {
        pending:   rawCounts.pending,
        draft:     rawCounts.draft,
        submitted: rawCounts.submitted + rawCounts.recheck,
        sendback:  rawCounts.sendback,
      };

  // ── Date filter ───────────────────────────────────────────────────────────

  const filterByDateRange = (doctor: Doctor): boolean => {
    if (!dateFilterValue.from || !dateFilterValue.to) return true;
    try {
      const doctorDate = dayjs(doctor.createdOn, "DD/MM/YYYY");
      const fromDate   = dayjs(dateFilterValue.from, "DD/MM/YYYY");
      const toDate     = dayjs(dateFilterValue.to,   "DD/MM/YYYY");
      return (
        doctorDate.isValid() &&
        fromDate.isValid() &&
        toDate.isValid() &&
        doctorDate.isBetween(fromDate, toDate, "day", "[]")
      );
    } catch {
      return true;
    }
  };

  // ── Filter + Sort ─────────────────────────────────────────────────────────

  const filteredAndSortedDoctors = () => {
    let result = [...doctors];

    if (selectedStatusFilter) {
      if (isMarketingHead && selectedStatusFilter === "pending") {
        result = result.filter(
          (doc) =>
        
            doc.doctorStatus?.toLowerCase() === "submitted"
        );
      } else if (!isMarketingHead && selectedStatusFilter === "submitted") {
        result = result.filter(
          (doc) =>
            doc.doctorStatus?.toLowerCase() === "submitted" ||
            doc.doctorStatus?.toLowerCase() === "recheck"
        );
      } else {
        result = result.filter(
          (doc) => doc.doctorStatus?.toLowerCase() === selectedStatusFilter
        );
      }
    }

    result = result.filter(filterByDateRange);

    if (filters.name) {
      result = result.filter((doc) =>
        doc.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }
    if (filters.empCode) {
  result = result.filter((doc) =>
    (doc.empCode || "")
      .toLowerCase()
      .includes(filters.empCode.toLowerCase())
  );
}

if (filters.Source) {
  result = result.filter((doc) =>
    (doc.source || "")
      .toLowerCase()
      .includes(filters.Source.toLowerCase())
  );
}
    if (filters.designation) {
      result = result.filter((doc) =>
        (doc.designation || "").toLowerCase().includes(filters.designation.toLowerCase())
      );
    }
    if (filters.specialization) {
      result = result.filter((doc) =>
        (doc.specialization || "").toLowerCase().includes(filters.specialization.toLowerCase())
      );
    }
    if (filters.experience) {
      const expValue = parseInt(filters.experience);
      if (!isNaN(expValue)) {
        result = result.filter((doc) => doc.experienceYears >= expValue);
      }
    }
    if (filters.createdOn) {
      result = result.filter((doc) => {
        try {
          const docDate    = dayjs(doc.createdOn);
          const filterDate = dayjs(filters.createdOn);
          return docDate.isSame(filterDate, "day");
        } catch {
          return true;
        }
      });
    }
    if (filters.createdBy) {
  result = result.filter((doc) =>
    (doc.createdBy || "")
      .toLowerCase()
      .includes(filters.createdBy.toLowerCase())
  );
}
    if (filters.overdueDays) {
      const overdueValue = parseInt(filters.overdueDays);
      if (!isNaN(overdueValue)) {
        result = result.filter((doc) => doc.overdueDays >= overdueValue);
      }
    }
    if (filters.locationName) {
      result = result.filter((doc) =>
        doc.locationName.toLowerCase().includes(filters.locationName.toLowerCase())
      );
    }
    if (filters.doctorStatus) {
      result = result.filter((doc) =>
        doc.doctorStatus.toLowerCase().includes(filters.doctorStatus.toLowerCase())
      );
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        const aRaw = a[sortConfig.key!];
        const bRaw = b[sortConfig.key!];

        // ✅ Handle createdOn date sort separately
        if (sortConfig.key === "createdOn") {
          const aDate = dayjs(aRaw as string);
          const bDate = dayjs(bRaw as string);
          if (!aDate.isValid()) return 1;
          if (!bDate.isValid()) return -1;
          return sortConfig.direction === "asc"
            ? aDate.diff(bDate)
            : bDate.diff(aDate);
        }

        // ✅ Coerce to string — eliminates null/undefined TS18047 errors
        const aStr = aRaw == null ? "" : String(aRaw);
        const bStr = bRaw == null ? "" : String(bRaw);

        if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  };

  const currentFilteredDoctors = filteredAndSortedDoctors();
  const totalPages = Math.ceil(currentFilteredDoctors.length / itemsPerPage);
  const paginatedDoctors = currentFilteredDoctors.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getSerialNumber = (index: number) =>
    (currentPage - 1) * itemsPerPage + index + 1;

const mapDoctorStatus = (status: string): StatusType => {
  const normalized = status.toLowerCase();

  // 🔹 Non-Marketing Head: treat recheck as submitted
  if (!isMarketingHead && normalized === "recheck") {
    return "submitted";
  }

  switch (normalized) {
    case "pending":
      return "pending";
    case "save draft":
      return "draft";
    case "send back":
      return "sendback";
    case "submitted":
      return isMarketingHead ? "pending" : "submitted";
    case "approved":
      return "approved";
    case "rejected":
      return "rejected";
    case "recheck":
      return "recheck";
    case "deactivate":
      return "deactivate";
       case "left":
      return "left";
    default:
      return "pending";
  }
};

  if (apiError){
    return (
      <div className="bg-gray-50 p-8">
        <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="text-red-600 mb-4">⚠️ Error</div>
          <p className="text-red-800">{apiError}</p>
          <button
            onClick={fetchDoctors}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4">

      {/* ── Modals ── */}
      {isEditModalOpen && selectedDoctorId !== null && (
        <EditProfilePopup
          selectedDoctorId={selectedDoctorId}
          onClose={handleCloseModal}
        />
      )}

      {isViewModalOpen && selectedDoctorId !== null && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm p-4"
          onClick={() => setIsViewModalOpen(false)}
        >
          <div
            className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <OriginalProfile doctorId={selectedDoctorId} />
          </div>
        </div>
      )}

      {showTATModal && selectedDoctorForTAT && (
        <DoctorProfileTAT
          doctorId={selectedDoctorForTAT.id}
          doctorName={selectedDoctorForTAT.name}
          isOpen={showTATModal}
          onClose={() => {
            setShowTATModal(false);
            setSelectedDoctorForTAT(null);
          }}
        />
      )}

      {/* ── Header ── */}
      <div className="mb-2">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900">
            Welcome, {userName}!
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setIsDateFilterOpen(true)}
              className={`flex items-center gap-2 px-4 py-1.5 bg-white border rounded-lg hover:bg-gray-50 transition-all shadow-sm ${
                dateFilterValue.type
                  ? "border-blue-300 bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                  : "border-gray-300 text-gray-700"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              {dateFilterValue.type && (
                <span className="text-sm font-medium">
                  {dateFilterValue.from} — {dateFilterValue.to}
                </span>
              )}
            </button>

            {(selectedStatusFilter ||
              Object.values(filters).some(Boolean) ||
              dateFilterValue.type) && (
              <Button variant="secondary" size="sm" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── Status Tiles ── */}
      <div
        className={`grid grid-cols-1 md:grid-cols-2 gap-2 mb-5 ${
          isMarketingHead ? "lg:grid-cols-7" : "lg:grid-cols-4"
        }`}
      >
        {isMarketingHead ? (
          <>
            {/* ── Marketing Head: 5 tiles ── */}

            <StatusTile
              title="Pending"
              count={tileCounts.pending}
              icon={PendingIcon}
              bgColor="bg-white"
              borderColor="border-l-yellow-600"
              textColor="text-yellow-600"
              onClick={() => handleStatusTileClick("pending")}
              isActive={selectedStatusFilter === "pending"}
            />
            <StatusTile
              title="Draft"
              count={tileCounts.draft}
              icon={DraftIcon}
              bgColor="bg-white"
              borderColor="border-l-gray-600"
              textColor="text-gray-600"
              onClick={() => handleStatusTileClick("save draft")}
              isActive={selectedStatusFilter === "save draft"}
            />
            <StatusTile
              title="Approved"
              count={tileCounts.approved ?? 0}
              icon={ApprovedIcon}
              bgColor="bg-white"
              borderColor="border-l-green-700"
              textColor="text-green-700"
              onClick={() => handleStatusTileClick("approved")}
              isActive={selectedStatusFilter === "approved"}
            />
            <StatusTile
              title="Send Back"
              count={tileCounts.sendback}
              icon={SendBackIcon}
              bgColor="bg-white"
              borderColor="border-l-orange-700"
              textColor="text-orange-700"
              onClick={() => handleStatusTileClick("send back")}
              isActive={selectedStatusFilter === "send back"}
            />
            <StatusTile
              title="Re-check"
              count={tileCounts.recheck ?? 0}
              icon={RecheckIcon}
              bgColor="bg-white"
              borderColor="border-l-indigo-900"
              textColor="text-indigo-900"
              onClick={() => handleStatusTileClick("recheck")}
              isActive={selectedStatusFilter === "recheck"}
            />
            <StatusTile
      title="Deactivated"
      count={tileCounts.deactivate ?? 0}
      icon={DeactivatedIcon}
      bgColor="bg-white"
      borderColor="border-l-gray-400"
      textColor="text-gray-500"
      onClick={() => handleStatusTileClick("deactivate")}  // Matches your API status
      isActive={selectedStatusFilter === "deactivate"}
    />
     <StatusTile
      title="Left"
      count={tileCounts.left ?? 0}
      icon={LeftIcon}
      bgColor="bg-white"
      borderColor="border-l-gray-400"
      textColor="text-gray-500"
      onClick={() => handleStatusTileClick("left")}  // Matches your API status
      isActive={selectedStatusFilter === "left"}
    />
          </>
        ) : (
          <>
            {/* ── Other Roles: 4 tiles ── */}

            <StatusTile
              title="Pending"
              count={tileCounts.pending}
              icon={PendingIcon}
              bgColor="bg-white"
              borderColor="border-l-yellow-600"
              textColor="text-yellow-600"
              onClick={() => handleStatusTileClick("pending")}
              isActive={selectedStatusFilter === "pending"}
            />
            <StatusTile
              title="Draft"
              count={tileCounts.draft}
              icon={DraftIcon}
              bgColor="bg-white"
              borderColor="border-l-gray-600"
              textColor="text-gray-600"
              onClick={() => handleStatusTileClick("save draft")}
              isActive={selectedStatusFilter === "save draft"}
            />
            <StatusTile
              title="Submitted"
              count={tileCounts.submitted ?? 0}
              icon={SubmittedIcon}
              bgColor="bg-white"
              borderColor="border-l-blue-700"
              textColor="text-blue-700"
              onClick={() => handleStatusTileClick("submitted")}
              isActive={selectedStatusFilter === "submitted"}
            />
            <StatusTile
              title="Send Back"
              count={tileCounts.sendback}
              icon={SendBackIcon}
              bgColor="bg-white"
              borderColor="border-l-orange-700"
              textColor="text-orange-700"
              onClick={() => handleStatusTileClick("send back")}
              isActive={selectedStatusFilter === "send back"}
            />
            
          </>
        )}
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        <div className="overflow-x-auto custom-scrollbar">
          <div className="min-h-[calc(100vh-260px)] max-h-[calc(100vh-260px)] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#ecf0f1] sticky top-0 z-20 font-normal">
                <tr>
                  <th className="px-3 py-3 text-left w-16">S.No</th>
                  <th className="px-3 py-3 text-left cursor-pointer group" onClick={() => handleSort("name")}>
                    <div className="flex items-center gap-2">
                      <span>Doctor Name</span>
                      <span className="text-blue-500">{sortIcon("name")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left cursor-pointer" onClick={() => handleSort("empCode")}>
  <div className="flex items-center gap-2">
    <span>Emp Code</span>
    <span className="text-blue-500">{sortIcon("empCode")}</span>
  </div>
</th>

<th className="px-3 py-3 text-left cursor-pointer" onClick={() => handleSort("source")}>
  <div className="flex items-center gap-2">
    <span>Source</span>
    <span className="text-blue-500">{sortIcon("source")}</span>
  </div>
</th>
                  <th className="px-3 py-3 text-left cursor-pointer" onClick={() => handleSort("designation")}>
                    <div className="flex items-center gap-2">
                      <span>Designation</span>
                      <span className="text-blue-500">{sortIcon("designation")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left cursor-pointer" onClick={() => handleSort("specialization")}>
                    <div className="flex items-center gap-2">
                      <span>Specialization</span>
                      <span className="text-blue-500">{sortIcon("specialization")}</span>
                    </div>
                  </th>
                  {/* <th className="px-3 py-3 text-left max-w-[100px] cursor-pointer" onClick={() => handleSort("experienceYears")}>
                    <div className="flex items-center gap-2">
                      <span>Experience</span>
                      <span className="text-blue-500">{sortIcon("experienceYears")}</span>
                    </div>
                  </th> */}
                  <th className="px-3 py-3 text-left cursor-pointer whitespace-nowrap" onClick={() => handleSort("createdOn")}>
                    <div className="flex items-center gap-2">
                      <span>Created On</span>
                      <span className="text-blue-500">{sortIcon("createdOn")}</span>
                    </div>
                  </th>
                    <th className="px-3 py-3 text-left cursor-pointer whitespace-nowrap" onClick={() => handleSort("createdBy")}>
                    <div className="flex items-center gap-2">
                      <span>Created By</span>
                      <span className="text-blue-500">{sortIcon("createdOn")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left cursor-pointer" onClick={() => handleSort("locationName")}>
                    <div className="flex items-center gap-2">
                      <span>Location</span>
                      <span className="text-blue-500">{sortIcon("locationName")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left cursor-pointer max-w-[100px]" onClick={() => handleSort("overdueDays")}>
                    <div className="flex items-center gap-2">
                      <span>Overdue</span>
                      <span className="text-blue-500">{sortIcon("overdueDays")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left cursor-pointer max-w-[100px]" onClick={() => handleSort("doctorStatus")}>
                    <div className="flex items-center gap-2">
                      <span>Status</span>
                      <span className="text-blue-500">{sortIcon("doctorStatus")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-center font-semibold">Actions</th>
                </tr>

                {/* ── Column filter inputs ── */}
                <tr className="bg-gray-50 border-t border-gray-200">
                  <th className="px-1 py-1 w-16"></th>
                  <th className="px-1 py-1">
                    <input
                      type="text"
                      placeholder="Filter by name..."
                      value={filters.name}
                      onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </th>
                  <th className="px-1 py-1">
  <input
    type="text"
    placeholder="Filter by Emp Code..."
    value={filters.empCode}
    onChange={(e) =>
      setFilters((prev) => ({ ...prev, empCode: e.target.value }))
    }
    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs"
  />
</th>

<th className="px-1 py-1">
  <input
    type="text"
    placeholder="Filter by Source..."
    value={filters.Source}
    onChange={(e) =>
      setFilters((prev) => ({ ...prev, Source: e.target.value }))
    }
    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs"
  />
</th>
                  <th className="px-1 py-1">
                    <input
                      type="text"
                      placeholder="Filter by designation..."
                      value={filters.designation}
                      onChange={(e) => setFilters((prev) => ({ ...prev, designation: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </th>
                  <th className="px-1 py-1">
                    <input
                      type="text"
                      placeholder="Filter by specialization..."
                      value={filters.specialization}
                      onChange={(e) => setFilters((prev) => ({ ...prev, specialization: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </th>
                  {/* <th className="px-1 py-1 max-w-[100px]">
                    <input
                      type="text"
                      placeholder="Min years..."
                      value={filters.experience}
                      onChange={(e) => setFilters((prev) => ({ ...prev, experience: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </th> */}
                  <th className="px-1 py-1 whitespace-nowrap">
                    <input
                      type="date"
                      value={filters.createdOn}
                      onChange={(e) => setFilters((prev) => ({ ...prev, createdOn: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </th>
                   <th className="px-1 py-1 whitespace-nowrap">
                    <input
                      type="text"
                      placeholder="Filter by Creator..."
                      value={filters.createdBy}
                      onChange={(e) => setFilters((prev) => ({ ...prev, createdBy: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </th>
                  <th className="px-1 py-1">
                    <input
                      type="text"
                      placeholder="Filter by location..."
                      value={filters.locationName}
                      onChange={(e) => setFilters((prev) => ({ ...prev, locationName: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </th>
                  <th className="px-1 py-1 max-w-[100px]">
                    <input
                      type="text"
                      placeholder="Min days..."
                      value={filters.overdueDays}
                      onChange={(e) => setFilters((prev) => ({ ...prev, overdueDays: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </th>
                  <th className="px-1 py-1 max-w-[100px]">
                    <input
                      type="text"
                      placeholder="Filter by status..."
                      value={filters.doctorStatus}
                      onChange={(e) => setFilters((prev) => ({ ...prev, doctorStatus: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#184A84]"></div>
                        <p className="text-gray-500">Loading doctors...</p>
                      </div>
                    </td>
                  </tr>
                ) : paginatedDoctors.length > 0 ? (
                  paginatedDoctors.map((doc, index) => (
                    <React.Fragment key={doc.doctorId}>
                      <tr
                        onClick={() => handleRowToggle(doc.doctorId)}
                        className={`cursor-pointer transition-all duration-300 hover:bg-blue-50 ${
                          expandedDoctorId === doc.doctorId ? "bg-blue-100" : ""
                        }`}
                      >
                        <td className="px-3 py-3 text-center">{getSerialNumber(index)}</td>
                        <td className="px-2 py-3 font-semibold text-gray-900">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs flex-shrink-0">
                              {doc.name.charAt(0)}
                            </div>
                            <span className="text-xs">
                              {doc.namePrefixCode && `${doc.namePrefixCode}\u00A0`}
                              {doc.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-xs">
  {doc.empCode || "-"}
</td>
<td className="px-3 py-3 text-xs">
  {doc.source || "-"}
</td>

                        <td className="px-3 py-3 text-xs">{doc.designation || "-"}</td>

                        <td className="px-3 py-3">
                          <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800">
                            {doc.specialization || "-"}
                          </span>
                        </td>

                        {/* <td className="px-3 py-3">
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            {doc.experienceYears} yrs
                          </span>
                        </td> */}

                        <td className="px-3 py-3 text-xs whitespace-nowrap">
                          {dayjs(doc.createdOn).format("DD MMM YY")}
                        </td>
                        <td className="px-3 py-3 text-xs">{doc.createdBy}</td>
                        <td className="px-3 py-3 text-xs">{doc.locationName}</td>
                        <td className="px-3 py-3">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              doc.overdueDays > 7
                                ? "bg-red-100 text-red-800"
                                : doc.overdueDays > 3
                                ? "bg-orange-100 text-orange-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {doc.overdueDays} days
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <StatusBadge status={mapDoctorStatus(doc.doctorStatus)} />
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex items-center justify-center gap-1">

                        
                            {doctorsData.isView && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleViewDoctor(doc.doctorId); }}
                                title={`View ${doc.name}`}
                                className="p-1 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-900 transition-all hover:scale-110"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                            )}

                         <div className="relative flex items-center justify-center">
  {/* 3-dot menu trigger — hidden for Marketing Head when doctor has left */}
  {!(isMarketingHead && doc.doctorStatus?.toLowerCase() === "left") && (
    <>
      <button
        onClick={(e) => toggleActionMenu(e, doc.doctorId)}
        className="p-1 rounded-full hover:bg-gray-100 transition-all hover:scale-110"
        title="Actions"
      >
        <svg
          className="w-5 h-5 text-gray-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10 3a2 2 0 110 4 2 2 0 010-4zm0 5a2 2 0 110 4 2 2 0 010-4zm0 5a2 2 0 110 4 2 2 0 010-4z" />
        </svg>
      </button>

      {openMenuDoctorId === doc.doctorId && (
        <div
          className="absolute right-0 top-2 mt-3 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <ul className="py-1">
            {doctorsData.isEdit && canEditDoctor(doc.doctorStatus) && (
              <li>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-green-50 text-green-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeActionMenu();
                    handleEditDoctor(doc.doctorId);
                  }}
                >
                  <span>✏️</span>
                  <span>Edit</span>
                </button>
              </li>
            )}

            {doctorsData.isDelete && (
              <li>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-red-50 text-red-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeActionMenu();
                    handleDeleteDoctor(doc.doctorId);
                  }}
                >
                  <span>🗑️</span>
                  <span>Delete</span>
                </button>
              </li>
            )}

            <li>
              <button
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-purple-50 text-purple-700"
                onClick={(e) => {
                  e.stopPropagation();
                  closeActionMenu();
                  handleAuditDoctor(doc.doctorId, doc.name);
                }}
              >
                <span>📜</span>
                <span>Audit trail</span>
              </button>
            </li>

            {isMarketingHead && (
              <li>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-blue-50 text-blue-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeActionMenu();
                    handleToggleDoctorStatus(doc.doctorId, doc.doctorStatus);
                  }}
                >
                  <span>🔁</span>
                  <span>
                    {doc.doctorStatus?.toLowerCase() === "a" || doc.doctorStatus?.toLowerCase() === "approved"
                      ? "Deactivate"
                      : "Activate"}
                  </span>
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </>
  )}
</div>



                          </div>
                        </td>
                      </tr>

                      {/* Expanded audit row */}
                      {expandedDoctorId === doc.doctorId && (
                        <tr className="bg-gray-50">
                          <td colSpan={10} className="p-0">
                            <div className="overflow-x-auto w-full custom-scrollbar">
                              <div className="max-w-[700px]">
                                <DoctorTATRow doctorId={doc.doctorId} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="text-xl font-medium text-gray-500">No doctors found</p>
                        <p className="text-gray-400">Try adjusting your search or filter criteria</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={currentFilteredDoctors.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          showItemsPerPage={true}
          itemsPerPageOptions={[30, 50, 100, 150]}
        />
      </div>

      {/* ── Date Filter Popup ── */}
      <DateFilterPopup
        isOpen={isDateFilterOpen}
        onClose={() => setIsDateFilterOpen(false)}
        initialValue={dateFilterValue}
        onApply={handleDateFilterApply}
      />
    </div>
  );
};

export default Home;
